/**
 * /account/restore?token=… — magic-link landing for the deletion
 * confirmation email's restore link.
 *
 * Public — no sign-in gate. The token IS the auth.
 *
 * Flow:
 *   1. Page loads with ?token=… in URL
 *   2. User clicks "Restore my account" to confirm intent
 *   3. POST /auth/account/restore with the token
 *   4. On 200: success state + sign-in link
 *   5. On 404/410: clear failure state with support contact
 *
 * We require the explicit click rather than firing the POST on
 * mount because email-client preview-loaders sometimes pre-fetch
 * links, and we don't want a preview to silently restore an account
 * the user no longer wants.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { friendlyError } from '@/lib/errors';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export function AccountRestorePage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = search.get('token') ?? '';
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'not_found' | 'expired' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!token) setStatus('error');
  }, [token]);

  const onConfirm = async () => {
    setStatus('submitting');
    setErrMsg(null);
    try {
      const r = await fetch(API_BASE + '/auth/account/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (r.status === 200) {
        setStatus('success');
        return;
      }
      if (r.status === 404) {
        setStatus('not_found');
        return;
      }
      if (r.status === 410) {
        setStatus('expired');
        return;
      }
      const body = await r.json().catch(() => null) as { error?: { message?: string } } | null;
      setErrMsg(body?.error?.message ?? `Server responded with ${r.status}.`);
      setStatus('error');
    } catch (e) {
      setErrMsg(friendlyError(e, 'AccountRestore'));
      setStatus('error');
    }
  };

  return (
    <main style={page}>
      <Link to="/" style={wordmarkLink}>
        <span className="serif" style={wordmarkText}>wydm</span>
        <span style={wordmarkDot} />
      </Link>

      {/* No token at all */}
      {!token && (
        <>
          <h1 className="serif" style={h1}>Restore link missing.</h1>
          <p style={para}>
            This page expects a <code style={code}>?token=…</code> in the URL.
            Use the link from the account-deletion confirmation email we sent
            you, or email <a href="mailto:support@wydm.app" style={inlineLink}>support@wydm.app</a> if
            you can't find it.
          </p>
        </>
      )}

      {/* Confirm state */}
      {token && status === 'idle' && (
        <>
          <h1 className="serif" style={h1}>Restore your <span style={{ fontStyle: 'italic' }}>account?</span></h1>
          <p style={para}>
            Tap below to undo your account deletion. Your profile becomes
            visible again, sessions remain signed-out (sign in from any
            device after restore).
          </p>
          <div style={noteBox}>
            <strong>A note on subscriptions:</strong> when you deleted, we
            severed any mobile subscription. Restoring doesn't
            auto-resubscribe. If you want Premium back, open the WYDM
            app and subscribe again from any premium-gated screen.
          </div>
          <button onClick={onConfirm} style={primaryBtn}>Restore my account</button>
          <button onClick={() => navigate('/')} style={ghostBtn}>Not now</button>
        </>
      )}

      {status === 'submitting' && (
        <>
          <h1 className="serif" style={h1}>Restoring…</h1>
          <p style={para}>Hang on a sec.</p>
        </>
      )}

      {status === 'success' && (
        <>
          <h1 className="serif" style={h1}>Welcome <span style={{ fontStyle: 'italic' }}>back</span>.</h1>
          <p style={para}>
            Your account is active again. Sign in from any device.
          </p>
          <Link to="/auth/signin" style={primaryLink}>Sign in →</Link>
        </>
      )}

      {status === 'not_found' && (
        <>
          <h1 className="serif" style={h1}>That link's been used.</h1>
          <p style={para}>
            Restore tokens are single-use. If you've already restored your
            account, just sign in. If you didn't restore it and need help,
            email <a href="mailto:support@wydm.app" style={inlineLink}>support@wydm.app</a>.
          </p>
          <Link to="/auth/signin" style={primaryLink}>Sign in →</Link>
        </>
      )}

      {status === 'expired' && (
        <>
          <h1 className="serif" style={h1}>The 30-day window has passed.</h1>
          <p style={para}>
            Your account was permanently deleted as scheduled. We can't
            recover it from this point. Create a new account from{' '}
            <Link to="/auth/signup" style={inlineLink}>wydm.app/auth/signup</Link>{' '}
            if you'd like to start fresh.
          </p>
        </>
      )}

      {status === 'error' && (
        <>
          <h1 className="serif" style={h1}>Something went wrong.</h1>
          <p style={para}>
            {errMsg ?? 'Unknown error.'} Try again, or email <a href="mailto:support@wydm.app" style={inlineLink}>support@wydm.app</a> with
            this URL and we'll sort it manually.
          </p>
        </>
      )}

      <div style={footer}>
        <Link to="/privacy" style={inlineLink}>Privacy</Link>{' · '}
        <Link to="/terms" style={inlineLink}>Terms</Link>{' · '}
        <Link to="/" style={inlineLink}>Home</Link>
      </div>
    </main>
  );
}

// ── styles ────────────────────────────────────────────────────────────

const page: React.CSSProperties = { maxWidth: 540, margin: '0 auto', padding: '80px 24px' };
const wordmarkLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'baseline', gap: 4, textDecoration: 'none', color: 'var(--ink)' };
const wordmarkText: React.CSSProperties = { fontSize: 28, fontWeight: 500, fontStyle: 'italic', letterSpacing: -0.5 };
const wordmarkDot: React.CSSProperties = { width: 8, height: 8, borderRadius: 8, background: 'var(--coral)', display: 'inline-block' };
const h1: React.CSSProperties = { fontSize: 40, fontWeight: 500, lineHeight: 1.1, marginTop: 32, marginBottom: 16, letterSpacing: -0.5 };
const para: React.CSSProperties = { fontSize: 16, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 20 };
const code: React.CSSProperties = { fontFamily: 'ui-monospace, monospace', fontSize: 13, background: 'rgba(43,29,20,0.05)', padding: '1px 6px', borderRadius: 4 };
const noteBox: React.CSSProperties = {
  background: 'rgba(232,106,76,0.06)', border: '1px solid rgba(232,106,76,0.18)',
  borderRadius: 12, padding: '14px 18px', fontSize: 14, color: 'var(--ink-2)',
  lineHeight: 1.55, marginBottom: 20,
};
const primaryBtn: React.CSSProperties = {
  background: 'var(--coral)', color: '#fff', border: 'none', borderRadius: 100,
  padding: '12px 28px', fontWeight: 600, fontSize: 15, cursor: 'pointer',
  marginRight: 10,
};
const ghostBtn: React.CSSProperties = {
  background: 'transparent', color: 'var(--ink-2)', border: '1px solid var(--line-strong)',
  borderRadius: 100, padding: '12px 22px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
const primaryLink: React.CSSProperties = {
  display: 'inline-block',
  background: 'var(--coral)', color: '#fff', borderRadius: 100,
  padding: '12px 28px', fontWeight: 600, fontSize: 15,
  textDecoration: 'none',
};
const inlineLink: React.CSSProperties = { color: 'var(--coral)', textDecoration: 'underline', fontWeight: 600 };
const footer: React.CSSProperties = { marginTop: 48, paddingTop: 24, borderTop: '1px solid var(--line)', fontSize: 13, color: 'var(--ink-3)' };
